import type {
  CreateMaterialType,
  MaterialType,
} from "@/api/materialTypeService";
import CloseButton from "@/components/buttons/CloseButton";
import { DefaultPrimaryButton, InformationModal } from "@/styles/globalstyles";
import {
  Box,
  Button,
  Divider,
  Modal,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  useCreateMaterialType,
  useUpdateMaterialType,
} from "@/hooks/materialTypeHook";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { getAxiosErrorMessage } from "@/utils/healper";
import { useToast } from "@/hooks/useToast";

interface SaveMaterialTypeModalProps {
  open: boolean;
  handleClose: () => void;
  selectedMaterialType: MaterialType | null;
}

const SaveMaterialTypeModal = ({
  open,
  handleClose,
  selectedMaterialType,
}: SaveMaterialTypeModalProps) => {
  const { showToast } = useToast();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState<CreateMaterialType>({
    material_type_name: "",
    description: "",
    is_active: true,
  });

  useEffect(() => {
    setFormData({
      material_type_name: selectedMaterialType?.material_type_name || "",
      description: selectedMaterialType?.description || "",
      is_active: selectedMaterialType?.is_active || false,
    });
  }, [selectedMaterialType]);

  const createMutation = useCreateMaterialType();
  const updateMutation = useUpdateMaterialType();

  useQueryHandler({
    isFetching: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error,
    errorMessage: getAxiosErrorMessage(
      createMutation.error || updateMutation.error
    ),
  });

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.material_type_name) {
      newErrors.material_type_name = "กรุณากรอกชื่อ Material Type";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = () => {
    if (!validateForm()) return;
    createMutation.mutate({ ...formData, is_active: true }, {
      onSuccess: () => {
        showToast("บันทึก Material Type สำเร็จ", "success");
        onClose();
      },
    });
  };

  const handleEdit = () => {
    if (!validateForm()) return;
    if (selectedMaterialType !== null) {
      updateMutation.mutate(
        { id: selectedMaterialType.id, data: formData },
        {
          onSuccess: () => {
            showToast("บันทึก Material Type สำเร็จ", "success");
            onClose();
          },
        }
      );
      onClose();
    }
  };

  const onClose = () => {
    setFormData({
      material_type_name: "",
      description: "",
      is_active: true,
    });
    setErrors({});
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...InformationModal, maxHeight: "90vh" }}>
        <CloseButton onClick={onClose} />
        <Typography variant="h2">
          {selectedMaterialType || selectedMaterialType == 0
            ? "แก้ไข Material Type"
            : "เพิ่ม Material Type"}
        </Typography>
        <Box p={1}>
          <Divider sx={{ my: 1 }} />
          <Box
            mt={1}
            display="flex"
            flexDirection="column"
            gap={1.5}
          >
            <TextField
              fullWidth
              label={
                <>
                  ชื่อ Material Type{" "}
                  <Typography component="span" color="error.main">
                    *
                  </Typography>
                </>
              }
              variant="outlined"
              sx={{ mt: 2 }}
              value={formData.material_type_name}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  material_type_name: e.target.value,
                });
                if (errors.material_type_name) {
                  setErrors({ ...errors, material_type_name: "" });
                }
              }}
              error={!!errors.material_type_name}
              helperText={errors.material_type_name}
            />
            <TextField
              fullWidth
              label="คำอธิบาย"
              variant="outlined"
              sx={{ mt: 2 }}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
            {selectedMaterialType && (
              <Box mt={2} display="flex" alignItems="center" gap={1}>
                <Switch
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
                <Typography fontWeight="bold">Active</Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            sx={DefaultPrimaryButton}
            onClick={selectedMaterialType !== null ? handleEdit : handleAdd}
          >
            บันทึก
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SaveMaterialTypeModal;
